import { Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { catchError, mergeMap, tap } from 'rxjs';

import { UiActions } from '@store/actions/ui.actions';

import { LeerkrachtenInitialState, LeerkrachtenStateModel } from '@store/models/leerkrachten.model';
import { LeerkrachtenActions } from '@store/actions/leerkrachten.actions';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { TranslateService } from '@ngx-translate/core';
import { Leerkracht, LeerkrachtI } from '@models/leerkracht.model';
import { ApiClientService } from '@services/api-client.service';

@State<LeerkrachtenStateModel>({
  name: 'leerkrachten',
  defaults: LeerkrachtenInitialState,
})
@Injectable()
export class LeerkrachtenState {
  constructor(private readonly apiDstService: ApiClientService,
              private readonly translate: TranslateService,
              private store: Store) {
  }

  @Action(LeerkrachtenActions.LoadLeerkrachten)
  getLeerkrachten(ctx: StateContext<LeerkrachtenStateModel>, action: LeerkrachtenActions.LoadLeerkrachten) {
    const state = ctx.getState();
    ctx.patchState({ ...state, loading: true, loaded: false, error: null });

    if (state.leerkrachten.length > 0
      && !action.forceReload
    ) {
      // Already loaded, data is in state. Do not load again, except when forceReload is true
      ctx.patchState({ ...state, loading: false, loaded: true, error: null });
      // ctx.patchState({ loading: false, loaded: true });
      return;
    }

    return this.apiDstService.getLeerkrachtenBeheerders().pipe(
      tap((leerkrachten: Leerkracht[]) => {
        const state = ctx.getState();
        // TODO - HK - Move to the LoadLeerkrachtenSuccess action?
        ctx.patchState({ ...state, loading: false, loaded: true, error: null, leerkrachten });
      }),
      mergeMap((leerkrachten: Leerkracht[]) => {
        ctx.patchState({ loaded: true });
        return ctx.dispatch(new LeerkrachtenActions.LoadLeerkrachtenSuccess(leerkrachten));
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        return ctx.dispatch(new LeerkrachtenActions.LoadLeerkrachtenFailure(error));
      }),
    );
  }

  @Action(LeerkrachtenActions.SelectLeerkracht)
  selectLeerkracht(
    ctx: StateContext<LeerkrachtenStateModel>,
    action: LeerkrachtenActions.SelectLeerkracht) {
    const state = ctx.getState();
    ctx.patchState({ ...state, selectedLeerkrachtId: action.payload });
  }

  @Action(LeerkrachtenActions.DeselectLeerkracht)
  deselectLeerkracht(
    ctx: StateContext<LeerkrachtenStateModel>) {
    ctx.patchState({ selectedLeerkrachtId: '' });
  }

  @Action(LeerkrachtenActions.CreateLeerkracht)
  createLeerkracht(
    ctx: StateContext<LeerkrachtenStateModel>,
    action: LeerkrachtenActions.CreateLeerkracht) {
    return this.apiDstService.createLeerkracht(action.leerkrachtInput).pipe(
      tap((leerkracht) => {
        const newLeerkracht = leerkracht as LeerkrachtI;
        ctx.setState(
          patch<LeerkrachtenStateModel>({
            leerkrachten: insertItem<LeerkrachtI>(newLeerkracht, 0),
          }),
        );
        this.store.dispatch(new UiActions.toasterShowSuccess({
          summary: newLeerkracht.displayNaam,
          message: this.translate.instant('TOASTER_MESSAGES.LEERKRACHT_CREATE_SUCCESS'),
        }));
      }),
      catchError((err) =>
        this.store.dispatch(new UiActions.toasterShowError({
          summary: '',
          message: this.translate.instant('TOASTER_MESSAGES.LEERKRACHT_CREATE_ERROR', { error: err.status }),
        })),
      ),
    );
  }

  @Action(LeerkrachtenActions.UpdateLeerkracht)
  updateLeerkracht(
    ctx: StateContext<LeerkrachtenStateModel>,
    action: LeerkrachtenActions.UpdateLeerkracht) {
    const state = ctx.getState();
    return this.apiDstService.updateLeerkracht(action.persoonId, action.leerkrachtInput).pipe(
      tap((leerkracht) => {
        const returnedLeerkracht = leerkracht as LeerkrachtI;
        // TODO - set onderwijslocatieId is important!
        // Better to have this ID from the backend!
        // returnedDeelnemer.onderwijslocatieId = action.OnderwijsLocatieId;
        const index = state.leerkrachten.findIndex(d => d.persoonId === action.persoonId);
        if (index > -1) {
          ctx.setState(
            patch<LeerkrachtenStateModel>({
              leerkrachten: updateItem<LeerkrachtI>(index, returnedLeerkracht),
            }),
          );
          this.store.dispatch(new UiActions.toasterShowSuccess({
            summary: returnedLeerkracht.displayNaam,
            message: this.translate.instant('TOASTER_MESSAGES.LEERKRACHT_UPDATE_SUCCESS'),
          }));
        }

      }),
      catchError((err) =>
        this.store.dispatch(new UiActions.toasterShowError({
          summary: '',
          message: this.translate.instant('TOASTER_MESSAGES.LEERKRACHT_UPDATE_ERROR', { error: err.status }),
        })),
      ),
    );
  }

  @Action(LeerkrachtenActions.DeleteLeerkracht)
  deleteLeerkracht(
    ctx: StateContext<LeerkrachtenStateModel>,
    action: LeerkrachtenActions.DeleteLeerkracht) {

    // return this.apiDstService.deleteLeerkrachtFromOnderwijslocaties(action.onderwijsLocatieId, action.leerkracht.persoonId).pipe(
    return this.apiDstService.deleteLeerkracht(action.leerkracht.persoonId).pipe(
      tap(() => {
          ctx.setState(
            patch<LeerkrachtenStateModel>({
              leerkrachten: removeItem<LeerkrachtI>((leerkracht) => leerkracht.persoonId === action.leerkracht.persoonId),
            }),
          );
          this.store.dispatch(new UiActions.toasterShowSuccess({
            summary: action.leerkracht.displayNaam,
            message: this.translate.instant('TOASTER_MESSAGES.LEERKRACHT_DELETE_SUCCESS'),
          }));
        },
      ),
      catchError((err) =>
        this.store.dispatch(new UiActions.toasterShowError({
          summary: action.leerkracht.displayNaam,
          message: this.translate.instant('TOASTER_MESSAGES.LEERKRACHT_DELETE_ERROR', { error: err.status }),
        })),
      ),
    );
  }
}
